import type { Address } from '@my/directory-api';
import { cn } from '@my/ui';
import { MapPin, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * A route-lane endpoint — one of the organisation's Locations (a Directory
 * address). Structured so an order's stop can be matched against it when
 * auto-charging: `locationId` is the identity, the rest is the snapshot the
 * BE stores alongside it (country / city are the fallback match keys for
 * stops that aren't booked against a catalogued location).
 */
export interface RouteEndpoint {
  /** Directory address id — the lane identity. */
  locationId: string;
  /** Friendly display — the location's name. */
  label: string;
  /** ISO 3166-1 alpha-2. */
  countryIsoCode: string;
  city: string;
  postcode: string;
  placeId: string;
  latitude: number | null;
  longitude: number | null;
}

/** Project a Directory address into a lane endpoint. */
function addressToEndpoint(a: Address): RouteEndpoint {
  return {
    locationId: a.id,
    label: a.name,
    countryIsoCode: a.countryIsoCode,
    city: a.city ?? '',
    postcode: a.postcode ?? '',
    placeId: a.placeId ?? '',
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
  };
}

/** Second line under a location's name — enough to tell two depots apart. */
function addressHint(a: Address): string {
  return [a.city, a.postcode, a.countryIsoCode].filter(Boolean).join(' · ');
}

/**
 * Compact in-grid picker for a lane endpoint. Empty → a typeahead over the
 * organisation's Locations; once picked → a chip showing the location + its
 * country code, with an × to clear and re-pick.
 *
 * <p>
 * Lanes used to be free-text Google regions. They are Locations now because
 * that is the same list the orders are booked against — a lane whose ends are
 * catalogued locations can be matched to a movement by id instead of by
 * hoping two spellings of a city agree.
 * </p>
 */
export function LaneEndpointPicker({
  value,
  onChange,
  options,
  placeholder,
  /** The other end of the lane — excluded so a lane can't start and end alike. */
  excludeLocationId,
  disabled = false,
}: {
  value: RouteEndpoint | null;
  onChange: (next: RouteEndpoint | null) => void;
  options: Address[];
  placeholder: string;
  excludeLocationId?: string | null;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click outside closes the list and drops a half-typed query.
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = options.filter((a) => a.id !== excludeLocationId);
    const hit = (a: Address) =>
      [a.name, a.code, a.city, a.postcode, a.countryIsoCode]
        .filter(Boolean)
        .some((f) => f!.toLowerCase().includes(q));
    return (q ? pool.filter(hit) : pool).slice(0, 50);
  }, [options, query, excludeLocationId]);

  if (value) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-2 py-1 text-xs">
        <MapPin className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
        <span className="font-medium text-slate-700">{value.label}</span>
        {value.countryIsoCode && (
          <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold text-slate-500">
            {value.countryIsoCode}
          </span>
        )}
        <button
          type="button"
          aria-label="Clear"
          onClick={() => onChange(null)}
          className="ml-0.5 text-slate-400 hover:text-red-600"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-w-[10rem]" ref={ref}>
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <input
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        className="w-full rounded-sm border border-slate-300 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-700 placeholder:italic placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
      />
      {isOpen && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-md">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-[11px] italic text-slate-400">
              {options.length === 0
                ? 'No locations yet — add them under Locations.'
                : 'No location matches that search.'}
            </li>
          ) : (
            matches.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  // mousedown wins the race against the input's blur.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(addressToEndpoint(a));
                    setQuery('');
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-slate-50',
                  )}
                >
                  <span className="text-xs font-medium text-slate-700">{a.name}</span>
                  <span className="text-[10px] text-slate-400">{addressHint(a)}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
